import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
    FileText, 
    Save, 
    Loader2,
    Eye,
    CheckCircle,
    Edit2,
    Settings,
    LayoutGrid,
    Image as ImageIcon,
    Upload,
    Trash2,
    ShieldCheck,
    Globe,
    Plus,
    X
} from 'lucide-react';
import { toast } from 'sonner';
import api, { settingsAPI, uploadsAPI } from '@/lib/api';

const PAGES = [
    { slug: 'bine-ai-venit', label: 'Bine ai venit', description: 'Mesaj pentru vizitatorii neautentificați', isSpecial: true },
    { slug: 'despre-noi', label: 'Despre noi', description: 'Informații despre Colectarium' },
    { slug: 'cum-functioneaza', label: 'Cum funcționează', description: 'Ghid pentru utilizatori' },
    { slug: 'termeni', label: 'Termeni și condiții', description: 'Termeni legali' },
    { slug: 'confidentialitate', label: 'Politica de confidențialitate', description: 'GDPR și protecția datelor' },
    { slug: 'ajutor', label: 'Ajutor', description: 'FAQ și suport' },
    { slug: 'cookie-policy', label: 'Cookie Policy', description: 'Politica de cookie-uri' },
    { slug: 'contact', label: 'Contact', description: 'Informații de contact' },
];

const ITEMS_PER_PAGE_OPTIONS = [12, 24, 36, 48, 60, 72, 96];
const GRID_COLUMNS_OPTIONS = [1, 2, 3, 4, 5, 6];

const AdminAspectTab = () => {
    const [selectedPage, setSelectedPage] = useState(null);
    const [pageContent, setPageContent] = useState({ title: '', content: '' });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Site settings
    const [siteSettings, setSiteSettings] = useState({
        listings_per_page: 48,
        auctions_per_page: 48,
        search_results_per_page: 48,
        grid_columns_desktop: 4,
        grid_columns_tablet: 3,
        grid_columns_mobile: 1,
        favicon_url: '',
    });
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    
    // Contact admin settings
    const [adminsList, setAdminsList] = useState([]);
    const [contactAdmin, setContactAdmin] = useState({ admin_user_id: '', admin_username: '', admin_name: '' });
    const [notifyNewUsers, setNotifyNewUsers] = useState(false);
    const [loadingContactAdmin, setLoadingContactAdmin] = useState(true);
    const [savingContactAdmin, setSavingContactAdmin] = useState(false);
    
    // Favicon upload
    const faviconInputRef = useRef(null);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);
    
    // Moderation settings
    const [moderationEnabled, setModerationEnabled] = useState(false);
    const [loadingModeration, setLoadingModeration] = useState(true);
    const [savingModeration, setSavingModeration] = useState(false);
    
    // Excluded domains for visitor tracking
    const [excludedDomains, setExcludedDomains] = useState([]);
    const [newDomain, setNewDomain] = useState('');
    const [loadingExcludedDomains, setLoadingExcludedDomains] = useState(true);
    const [savingExcludedDomains, setSavingExcludedDomains] = useState(false);
    
    // Load site settings on mount
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const res = await settingsAPI.get();
                setSiteSettings({
                    listings_per_page: res.data.listings_per_page || 48,
                    auctions_per_page: res.data.auctions_per_page || 48,
                    search_results_per_page: res.data.search_results_per_page || 48,
                    grid_columns_desktop: res.data.grid_columns_desktop || 4,
                    grid_columns_tablet: res.data.grid_columns_tablet || 3,
                    grid_columns_mobile: res.data.grid_columns_mobile || 1,
                    favicon_url: res.data.favicon_url || '',
                });
            } catch (error) {
                console.error('Error loading settings:', error);
            } finally {
                setLoadingSettings(false);
            }
        };
        loadSettings();
    }, []);
    
    // Load contact admin settings
    useEffect(() => {
        const loadContactAdmin = async () => {
            try {
                const [adminRes, listRes] = await Promise.all([
                    settingsAPI.getContactAdmin(),
                    settingsAPI.getAdminsList()
                ]);
                setContactAdmin(adminRes.data || {});
                setNotifyNewUsers(adminRes.data?.notify_new_users || false);
                setAdminsList(listRes.data || []);
            } catch (error) {
                console.error('Error loading contact admin:', error);
            } finally {
                setLoadingContactAdmin(false);
            }
        };
        loadContactAdmin();
    }, []);
    
    // Load moderation settings
    useEffect(() => {
        const loadModerationSettings = async () => {
            try {
                const res = await api.get('/settings/moderation');
                setModerationEnabled(res.data?.moderation_enabled || false);
            } catch (error) {
                console.error('Error loading moderation settings:', error);
            } finally {
                setLoadingModeration(false);
            }
        };
        loadModerationSettings();
    }, []);
    
    // Load excluded domains
    useEffect(() => {
        const loadExcludedDomains = async () => {
            try {
                const res = await api.get('/settings/excluded-domains');
                setExcludedDomains(res.data?.domains || []);
            } catch (error) {
                console.error('Error loading excluded domains:', error);
            } finally {
                setLoadingExcludedDomains(false);
            }
        };
        loadExcludedDomains();
    }, []);
    
    const handleAddExcludedDomain = async () => {
        if (!newDomain.trim()) return;
        
        const domain = newDomain.trim().toLowerCase();
        if (excludedDomains.includes(domain)) {
            toast.error('Acest domeniu există deja în listă');
            return;
        }
        
        setSavingExcludedDomains(true);
        try {
            const updatedDomains = [...excludedDomains, domain];
            await api.put('/settings/excluded-domains', { domains: updatedDomains });
            setExcludedDomains(updatedDomains);
            setNewDomain('');
            toast.success('Domeniu adăugat');
        } catch (error) {
            console.error('Error adding excluded domain:', error);
            toast.error('Eroare la adăugarea domeniului');
        } finally {
            setSavingExcludedDomains(false);
        }
    };
    
    const handleRemoveExcludedDomain = async (domain) => {
        setSavingExcludedDomains(true);
        try {
            const updatedDomains = excludedDomains.filter(d => d !== domain);
            await api.put('/settings/excluded-domains', { domains: updatedDomains });
            setExcludedDomains(updatedDomains);
            toast.success('Domeniu eliminat');
        } catch (error) {
            console.error('Error removing excluded domain:', error);
            toast.error('Eroare la eliminarea domeniului');
        } finally {
            setSavingExcludedDomains(false);
        }
    };
    
    const handleSaveModerationSettings = async (enabled) => {
        setSavingModeration(true);
        try {
            await api.put('/settings/moderation', { moderation_enabled: enabled });
            setModerationEnabled(enabled);
            toast.success(enabled 
                ? 'Moderarea conținutului a fost activată' 
                : 'Moderarea conținutului a fost dezactivată'
            );
        } catch (error) {
            console.error('Error saving moderation settings:', error);
            toast.error('Eroare la salvarea setării');
            // Revert on error
            setModerationEnabled(!enabled);
        } finally {
            setSavingModeration(false);
        }
    };
    
    const handleSaveContactAdmin = async () => {
        if (!contactAdmin.admin_user_id) {
            toast.error('Selectează un administrator');
            return;
        }
        setSavingContactAdmin(true);
        try {
            await settingsAPI.setContactAdmin({ 
                admin_user_id: contactAdmin.admin_user_id,
                notify_new_users: notifyNewUsers
            });
            toast.success('Setările administratorului au fost salvate');
        } catch (error) {
            console.error('Error saving contact admin:', error);
            toast.error('Eroare la salvarea setării');
        } finally {
            setSavingContactAdmin(false);
        }
    };
    
    const handleSaveSettings = async () => {
        setSavingSettings(true);
        try {
            await settingsAPI.update(siteSettings);
            toast.success('Setările au fost salvate');
            // Update favicon in document
            if (siteSettings.favicon_url) {
                updateFavicon(siteSettings.favicon_url);
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Eroare la salvarea setărilor');
        } finally {
            setSavingSettings(false);
        }
    };
    
    // Update favicon in document head
    const updateFavicon = (url) => {
        const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = url;
        document.getElementsByTagName('head')[0].appendChild(link);
    };
    
    // Handle favicon upload
    const handleFaviconUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validate file type
        const validTypes = ['image/png', 'image/x-icon', 'image/ico', 'image/jpeg', 'image/svg+xml'];
        if (!validTypes.includes(file.type) && !file.name.endsWith('.ico')) {
            toast.error('Formatul trebuie să fie PNG, ICO, JPG sau SVG');
            return;
        }
        
        // Validate file size (max 1MB)
        if (file.size > 1024 * 1024) {
            toast.error('Fișierul trebuie să fie mai mic de 1MB');
            return;
        }
        
        setUploadingFavicon(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await uploadsAPI.uploadImage(formData);
            
            const newFaviconUrl = res.data.url;
            setSiteSettings(prev => ({ ...prev, favicon_url: newFaviconUrl }));
            
            // Save immediately
            await settingsAPI.update({ ...siteSettings, favicon_url: newFaviconUrl });
            updateFavicon(newFaviconUrl);
            
            toast.success('Favicon actualizat cu succes');
        } catch (error) {
            console.error('Error uploading favicon:', error);
            toast.error('Eroare la încărcarea favicon-ului');
        } finally {
            setUploadingFavicon(false);
            if (faviconInputRef.current) {
                faviconInputRef.current.value = '';
            }
        }
    };
    
    // Remove favicon
    const handleRemoveFavicon = async () => {
        try {
            setSiteSettings(prev => ({ ...prev, favicon_url: '' }));
            await settingsAPI.update({ ...siteSettings, favicon_url: '' });
            // Reset to default favicon
            updateFavicon('/favicon.ico');
            toast.success('Favicon resetat la implicit');
        } catch (error) {
            console.error('Error removing favicon:', error);
            toast.error('Eroare la ștergerea favicon-ului');
        }
    };

    const loadPage = async (slug) => {
        setLoading(true);
        try {
            const res = await api.get(`/pages/${slug}`);
            setPageContent({
                title: res.data.title || '',
                content: res.data.content || ''
            });
            setSelectedPage(slug);
        } catch (error) {
            console.error('Error loading page:', error);
            // Load default
            const page = PAGES.find(p => p.slug === slug);
            setPageContent({
                title: page?.label || '',
                content: ''
            });
            setSelectedPage(slug);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedPage) return;
        
        setSaving(true);
        try {
            await api.put(`/pages/${selectedPage}`, pageContent);
            toast.success('Pagina a fost salvată');
        } catch (error) {
            console.error('Error saving page:', error);
            toast.error('Eroare la salvarea paginii');
        } finally {
            setSaving(false);
        }
    };

    const handlePreview = () => {
        if (selectedPage) {
            window.open(`/${selectedPage}`, '_blank');
        }
    };

    return (
        <div className="space-y-6">
            {/* Site Settings Card - Grid and Favicon only */}
            <Card className="rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <LayoutGrid className="w-5 h-5" />
                        Setări Afișare
                    </CardTitle>
                    <CardDescription>
                        Configurează aspectul grilei de listări
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loadingSettings ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Grid Columns Settings */}
                            <div>
                                <Label className="text-base font-semibold mb-4 block">Listări per rând</Label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="grid-desktop">Desktop (≥1280px)</Label>
                                        <Select 
                                            value={String(siteSettings.grid_columns_desktop)} 
                                            onValueChange={(val) => setSiteSettings(prev => ({...prev, grid_columns_desktop: parseInt(val)}))}
                                        >
                                            <SelectTrigger id="grid-desktop" data-testid="grid-desktop-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {GRID_COLUMNS_OPTIONS.map((num) => (
                                                    <SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'coloană' : 'coloane'}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="grid-tablet">Tabletă (768px - 1279px)</Label>
                                        <Select 
                                            value={String(siteSettings.grid_columns_tablet)} 
                                            onValueChange={(val) => setSiteSettings(prev => ({...prev, grid_columns_tablet: parseInt(val)}))}
                                        >
                                            <SelectTrigger id="grid-tablet" data-testid="grid-tablet-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {GRID_COLUMNS_OPTIONS.map((num) => (
                                                    <SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'coloană' : 'coloane'}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="grid-mobile">Mobil (&lt;768px)</Label>
                                        <Select 
                                            value={String(siteSettings.grid_columns_mobile)} 
                                            onValueChange={(val) => setSiteSettings(prev => ({...prev, grid_columns_mobile: parseInt(val)}))}
                                        >
                                            <SelectTrigger id="grid-mobile" data-testid="grid-mobile-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {GRID_COLUMNS_OPTIONS.map((num) => (
                                                    <SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'coloană' : 'coloane'}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Favicon Section */}
                            <div className="pt-4 border-t">
                                <Label className="text-base font-medium flex items-center gap-2 mb-4">
                                    <ImageIcon className="w-4 h-4" />
                                    Site Icon (Favicon)
                                </Label>
                                <div className="flex items-center gap-4">
                                    {/* Preview */}
                                    <div className="w-16 h-16 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                                        {siteSettings.favicon_url ? (
                                            <img 
                                                src={siteSettings.favicon_url} 
                                                alt="Favicon" 
                                                className="w-12 h-12 object-contain"
                                            />
                                        ) : (
                                            <ImageIcon className="w-6 h-6 text-gray-400" />
                                        )}
                                    </div>
                                    
                                    {/* Upload/Remove buttons */}
                                    <div className="flex flex-col gap-2">
                                        <input
                                            ref={faviconInputRef}
                                            type="file"
                                            accept=".ico,.png,.jpg,.jpeg,.svg,image/x-icon,image/png,image/jpeg,image/svg+xml"
                                            onChange={handleFaviconUpload}
                                            className="hidden"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => faviconInputRef.current?.click()}
                                            disabled={uploadingFavicon}
                                            className="rounded-full"
                                            data-testid="upload-favicon-btn"
                                        >
                                            {uploadingFavicon ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Upload className="w-4 h-4 mr-2" />
                                            )}
                                            {siteSettings.favicon_url ? 'Schimbă' : 'Încarcă'}
                                        </Button>
                                        {siteSettings.favicon_url && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={handleRemoveFavicon}
                                                className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                                                data-testid="remove-favicon-btn"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Șterge
                                            </Button>
                                        )}
                                    </div>
                                    
                                    {/* Info */}
                                    <p className="text-xs text-muted-foreground max-w-xs">
                                        Format recomandat: PNG sau ICO, 32x32px sau 64x64px, max 1MB
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex justify-end">
                                <Button 
                                    onClick={handleSaveSettings}
                                    disabled={savingSettings}
                                    className="rounded-full"
                                    data-testid="save-site-settings-btn"
                                >
                                    {savingSettings ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    Salvează setările
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Contact Admin Settings Card */}
            <Card className="rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Administrator pentru Contact
                    </CardTitle>
                    <CardDescription>
                        Selectează administratorul care va primi mesajele de la utilizatori prin funcția "Contactează admin"
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loadingContactAdmin ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Administrator destinatar</Label>
                                <Select 
                                    value={contactAdmin.admin_user_id || ''} 
                                    onValueChange={(val) => {
                                        const selected = adminsList.find(a => a.user_id === val);
                                        setContactAdmin({
                                            admin_user_id: val,
                                            admin_username: selected?.username || '',
                                            admin_name: selected?.name || ''
                                        });
                                    }}
                                >
                                    <SelectTrigger className="w-full max-w-md">
                                        <SelectValue placeholder="Selectează un administrator" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {adminsList.map((admin) => (
                                            <SelectItem key={admin.user_id} value={admin.user_id}>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">{admin.name || admin.username}</span>
                                                    <span className="text-xs text-muted-foreground">({admin.role})</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Acest administrator va primi toate mesajele trimise prin opțiunea "Contactează admin" din meniul utilizatorilor.
                                </p>
                            </div>
                            
                            {/* Notify about new users checkbox */}
                            <div className="flex items-center space-x-3 pt-4 border-t">
                                <input
                                    type="checkbox"
                                    id="notify-new-users"
                                    checked={notifyNewUsers}
                                    onChange={(e) => setNotifyNewUsers(e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <Label htmlFor="notify-new-users" className="cursor-pointer">
                                    Anunță administratorul pe email despre utilizatori noi înscriși
                                </Label>
                            </div>
                            <p className="text-xs text-muted-foreground -mt-2 ml-7">
                                Administratorul selectat va primi un email de fiecare dată când un utilizator nou se înregistrează pe platformă.
                            </p>
                            
                            <div className="flex justify-end">
                                <Button 
                                    onClick={handleSaveContactAdmin}
                                    disabled={savingContactAdmin || !contactAdmin.admin_user_id}
                                    className="rounded-full"
                                >
                                    {savingContactAdmin ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    Salvează
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Content Moderation Settings Card */}
            <Card className="rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5" />
                        Moderare Conținut
                    </CardTitle>
                    <CardDescription>
                        Controlează dacă listările noi trebuie aprobate înainte de a deveni publice
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loadingModeration ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                                <div className="space-y-1">
                                    <Label htmlFor="moderation-toggle" className="text-base font-medium cursor-pointer">
                                        Activează moderarea conținutului
                                    </Label>
                                    <p className="text-sm text-muted-foreground">
                                        Când este activată, listările noi ale utilizatorilor vor avea status "în așteptare" și vor trebui aprobate de un moderator înainte de a fi vizibile public.
                                    </p>
                                </div>
                                <Switch
                                    id="moderation-toggle"
                                    checked={moderationEnabled}
                                    onCheckedChange={(checked) => handleSaveModerationSettings(checked)}
                                    disabled={savingModeration}
                                    data-testid="moderation-toggle"
                                />
                            </div>
                            
                            {moderationEnabled && (
                                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                                    <p className="text-sm text-amber-800">
                                        <strong>Moderarea este activă.</strong> Listările noi ale utilizatorilor (exceptând administratorii și moderatorii) vor fi plasate în coada de aprobare. 
                                        Vizitează tab-ul <strong>"Moderare"</strong> pentru a gestiona listările în așteptare.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Excluded Domains Card */}
            <Card className="rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Globe className="w-5 h-5" />
                        Domenii Excluse din Vizite
                    </CardTitle>
                    <CardDescription>
                        Vizitele de la aceste domenii/IP-uri nu vor fi luate în calcul în statisticile de trafic
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loadingExcludedDomains ? (
                        <div className="flex items-center gap-2 py-4">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm text-muted-foreground">Se încarcă...</span>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Add new domain */}
                            <div className="flex gap-2">
                                <Input
                                    placeholder="ex: googleusercontent.com sau 192.168.1.1"
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddExcludedDomain()}
                                    className="flex-1"
                                    data-testid="excluded-domain-input"
                                />
                                <Button 
                                    onClick={handleAddExcludedDomain}
                                    disabled={savingExcludedDomains || !newDomain.trim()}
                                    data-testid="add-excluded-domain-btn"
                                >
                                    {savingExcludedDomains ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Plus className="w-4 h-4 mr-1" />
                                            Adaugă
                                        </>
                                    )}
                                </Button>
                            </div>
                            
                            {/* List of excluded domains */}
                            {excludedDomains.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-2">
                                    Nu există domenii excluse. Adaugă domenii pentru a le exclude din statisticile de trafic.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    <Label className="text-sm text-muted-foreground">
                                        Domenii excluse ({excludedDomains.length}):
                                    </Label>
                                    <div className="flex flex-wrap gap-2">
                                        {excludedDomains.map((domain) => (
                                            <Badge 
                                                key={domain} 
                                                variant="secondary"
                                                className="flex items-center gap-1 py-1 px-2"
                                            >
                                                <span className="font-mono text-xs">{domain}</span>
                                                <button
                                                    onClick={() => handleRemoveExcludedDomain(domain)}
                                                    className="ml-1 hover:text-destructive transition-colors"
                                                    disabled={savingExcludedDomains}
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            <p className="text-xs text-muted-foreground mt-2">
                                Vizitatorii cu hostname sau IP care conține unul din aceste domenii vor fi ascunși din statistici.
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Static Pages Card */}
            <Card className="rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Pagini Statice
                    </CardTitle>
                    <CardDescription>
                        Editează conținutul paginilor informative ale site-ului
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Page List */}
                        <div className="space-y-2">
                            <Label>Selectează pagina</Label>
                            <div className="space-y-2">
                                {PAGES.map((page) => (
                                    <button
                                        key={page.slug}
                                        onClick={() => loadPage(page.slug)}
                                        className={`w-full p-3 text-left rounded-lg border transition-all ${
                                            selectedPage === page.slug 
                                                ? 'border-primary bg-primary/5' 
                                                : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="font-medium">{page.label}</p>
                                                <p className="text-xs text-muted-foreground">{page.description}</p>
                                            </div>
                                            {selectedPage === page.slug && (
                                                <Badge variant="outline" className="text-primary border-primary">
                                                    <Edit2 className="w-3 h-3 mr-1" />
                                                    Editare
                                                </Badge>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Editor */}
                        <div className="lg:col-span-2 space-y-4">
                            {loading ? (
                                <div className="h-[500px] flex items-center justify-center">
                                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                </div>
                            ) : selectedPage ? (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="page-title">Titlu pagină</Label>
                                        <Input
                                            id="page-title"
                                            value={pageContent.title}
                                            onChange={(e) => setPageContent(prev => ({ ...prev, title: e.target.value }))}
                                            placeholder="Titlul paginii"
                                        />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label htmlFor="page-content">Conținut (Markdown)</Label>
                                            <div className="text-xs text-muted-foreground">
                                                # Titlu | ## Subtitlu | - Lista | **bold**
                                            </div>
                                        </div>
                                        <Textarea
                                            id="page-content"
                                            value={pageContent.content}
                                            onChange={(e) => setPageContent(prev => ({ ...prev, content: e.target.value }))}
                                            placeholder="Conținutul paginii în format Markdown..."
                                            className="min-h-[400px] font-mono text-sm"
                                        />
                                    </div>

                                    <div className="flex items-center justify-end gap-3">
                                        <Button 
                                            variant="outline" 
                                            onClick={handlePreview}
                                            className="rounded-full"
                                        >
                                            <Eye className="w-4 h-4 mr-2" />
                                            Previzualizează
                                        </Button>
                                        <Button 
                                            onClick={handleSave}
                                            disabled={saving}
                                            className="rounded-full"
                                        >
                                            {saving ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Save className="w-4 h-4 mr-2" />
                                            )}
                                            Salvează
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div className="h-[500px] flex items-center justify-center text-muted-foreground">
                                    <div className="text-center">
                                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                        <p>Selectează o pagină din stânga pentru a o edita</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="rounded-xl border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                            <p className="font-medium text-blue-900">Sfaturi pentru editare</p>
                            <ul className="text-sm text-blue-700 mt-1 space-y-1">
                                <li>• Folosește <code className="bg-blue-100 px-1 rounded"># Titlu</code> pentru titluri mari</li>
                                <li>• Folosește <code className="bg-blue-100 px-1 rounded">## Subtitlu</code> pentru secțiuni</li>
                                <li>• Folosește <code className="bg-blue-100 px-1 rounded">- element</code> pentru liste</li>
                                <li>• Modificările sunt afișate imediat pe site după salvare</li>
                            </ul>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminAspectTab;
